/**
 * @module hooks/dva/useQuerySelectorDva
 * @description DVA 版 useQuerySelector — 从 DVA store 的 Analysis/MultiAnalysis model 读取查询状态
 *
 * 签名与 V2 useQuerySelector 完全一致：(selector, equalityFn?) => T
 * 数据来源：state[CHART_OLD_NAMESPACE] — 即 model.ts 的全局 model
 *
 * @see docs/DVA环境迁移执行计划.md §N1
 */

import { shallowEqual, useSelector } from 'umi';
import { CHART_OLD_NAMESPACE } from '@/pages/datalab/models/namespaces';
import type {
  IMultidimensionalAnalysisState,
  IOldAnalysisModelState,
} from '@/pages/datalab/models/types/state';

/**
 * DVA 版 useQuerySelector
 *
 * @param selector - 从 model state 中选择需要的值
 * @param equalityFn - 默认 shallowEqual
 *
 * ## 限制：不适合直接订阅 columnDimensions / columnMetrics
 *
 * model 在 updateTableField 中先 Object.assign 再 put([...columnMetrics])。
 * shallowEqual 比较元素引用会跳过更新；fieldListSignature 作 equalityFn 也会因 prev/next
 * 读到同一批已 mutate 对象而误判。请改用 useQueryColumnDimensions / useQueryColumnMetrics
 *（内部用数组引用 === 判断）。
 *
 * @see ./useQueryColumnLists.ts
 */
export function useQuerySelector<T>(
  selector: (state: IOldAnalysisModelState) => T,
  equalityFn: (left: T, right: T) => boolean = shallowEqual,
): T {
  return useSelector(
    (rootState: IMultidimensionalAnalysisState) => {
      const modelState = rootState[CHART_OLD_NAMESPACE];
      return selector(modelState ?? ({} as IOldAnalysisModelState));
    },
    equalityFn,
  );
}

export { shallowEqual as querySelectorShallowEqual };
